import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useId } from 'react';
import {
  getCardStyles,
  type CardComponentProps,
} from 'src/components/data-display/card';
import { cn } from 'src/utils/cn';

export function withNestedClickHandler(
  onClick: (event: MouseEvent<HTMLButtonElement>) => void
) {
  return {
    onClick(e: MouseEvent<HTMLButtonElement>) {
      e.stopPropagation();
      onClick(e);
    },
    onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation();
        e.preventDefault();
        e.currentTarget.click();
      }
    },
  };
}

const ExpandedCaret: FC<{ expanded: boolean }> = ({ expanded }) => {
  return (
    <FontAwesomeIcon
      icon={expanded ? faCaretUp : faCaretDown}
      aria-hidden="true"
      data-testid="card-open-close-caret"
      className="text-body-sm!"
    />
  );
};

export type ExpandableCardProps = {
  isExpanded: boolean;
  setExpanded: (expanded: boolean) => void;
  header?: ReactNode;
  icon?: ReactNode;
  headerMaxSpace?: boolean;
  children: ReactNode;
  dataTestId?: string;
  dataHelpAnnotation?: string;
};

export function getExpandableCardHeaderStyles({
  mode = 'default',
  isExpanded,
}: {
  mode?: CardComponentProps['mode'];
  isExpanded: ExpandableCardProps['isExpanded'];
}) {
  return cn(
    getCardStyles({ mode }),
    `
      cursor-pointer transition-colors duration-150
      hover:bg-background-surface-hover
      focus-visible:bg-background-surface-hover
    `,
    isExpanded && 'rounded-b-none',
    `
      legacy:hover:bg-legacy-brand-light-4
      legacy:focus-visible:bg-legacy-brand-light-4
    `
  );
}

export const ExpandableCard: FC<ExpandableCardProps> = ({
  isExpanded,
  setExpanded,
  header = '',
  icon,
  children,
  headerMaxSpace = false,
  dataTestId,
  dataHelpAnnotation,
}) => {
  const id = useId();

  //  div as a container in order to be safely used as a child of "display: flex | grid"
  return (
    <div>
      <div
        role="button"
        className={cn(
          `select-none`,
          getExpandableCardHeaderStyles({ isExpanded })
        )}
        aria-expanded={isExpanded}
        aria-controls={id}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setExpanded(!isExpanded);
            // .preventDefault() required because of role=”button”
            e.preventDefault();
          }
        }}
        onClick={() => setExpanded(!isExpanded)}
        data-testid={
          dataTestId ? `${dataTestId}-header` : 'card-header-wrapper'
        }
      >
        <div
          className={cn(
            `
              grid grid-flow-col items-center justify-between gap-xl
              legacy:gap-[1.5rem]
            `,
            headerMaxSpace && 'grid-cols-[1fr_min-content]'
          )}
          data-help-annotation={dataHelpAnnotation}
        >
          <div data-testid="card-header">{header}</div>

          {icon ? (
            <div
              className={`
                grid grid-flow-col items-center gap-2xs
                legacy:gap-[0.2rem]
              `}
            >
              {icon}
              <ExpandedCaret expanded={isExpanded} />
            </div>
          ) : (
            <ExpandedCaret expanded={isExpanded} />
          )}
        </div>
      </div>

      {isExpanded && (
        <div
          className={cn(
            getCardStyles({ mode: 'default' }),
            `rounded-t-none border-t-0`
          )}
          data-testid={dataTestId ? `${dataTestId}-body` : undefined}
          id={id}
        >
          {children}
        </div>
      )}
    </div>
  );
};
